// ============================================================================
// Local template preview — the create-mode twin of the BACKEND preview.
// ----------------------------------------------------------------------------
// `preview_workflow_template` (backend/services/workflow_template_service.py)
// computes a template's timeline server-side, but it needs a SAVED template to
// load. In create mode the steps only exist client-side, so this module
// re-implements the same computation over the local steps — every rule below
// (anchor resolution, business-day walking, null endDate for duration-less
// steps, plannedDate sort, overlap warnings, today+60/+30 anchor defaults)
// mirrors that function, NOT the museum-branch demo, which drifted from it.
//
// The mirror is pinned by shared/template-preview-parity.json: a jest suite
// runs this module and a pytest suite runs the real backend function over the
// SAME fixture. Change either implementation and its side of the pair reddens.
//
// Known backend discrepancy, mirrored on purpose: OffsetRule carries a
// `referenceStepId`, but the backend preview ignores it — `after_previous_step`
// always chains from the PREVIOUS step in step order, whatever the reference
// says. Parity over cleverness; the referenceStepId gap belongs to the
// template-engine backlog, not to this module.
//
// Dates are computed as pure calendar days in UTC (the JS stand-in for
// Python's `datetime.date`) so DST shifts can never move a planned date.
// ============================================================================

/** One step to preview — the camelCase twin of a workflow_template_steps row. */
export interface LocalPreviewStep {
  stepId: string;
  stepOrder: number;
  name: string;
  discipline?: string | null;
  category?: string | null;
  /** The STORED offset type (absolute | business_days | relative) — echoed raw in the timeline. */
  offsetType: string;
  /**
   * Always numeric here: the DB column allows NULL (the backend echoes it raw
   * and computes with `or 0`), but every UI-authored step carries a number, so
   * the local domain is narrowed to keep previewData's typing sound.
   */
  offsetDays: number;
  /** Local create-mode steps have no duration field; null/0 renders milestone-style. */
  durationDays?: number | null;
  /** The effective anchor (metadata.anchorType on a saved step). Falls back to offsetType. */
  anchorType?: string | null;
}

export interface LocalPreviewInput {
  templateName: string;
  steps: LocalPreviewStep[];
  /** Anchor dates as YYYY-MM-DD strings; unknown keys are custom anchors, exactly as the API payload. */
  anchors: Record<string, string | undefined>;
}

export interface LocalPreviewTimelineEntry {
  stepId: string;
  stepOrder: number;
  name: string;
  discipline: string | null;
  category: string | null;
  offsetType: string;
  offsetDays: number;
  durationDays: number | null;
  plannedDate: string;
  endDate: string | null;
  anchorType: string;
}

export interface LocalPreviewResult {
  templateName: string;
  anchors: Record<string, string>;
  timeline: LocalPreviewTimelineEntry[];
  warnings: string[];
  /** Local previews have no tenant; kept so the result satisfies PreviewTemplateResponse. */
  sept_tenantid: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Strict YYYY-MM-DD parse to a UTC calendar date, returning null on anything
 * else — the shape the backend's custom-anchor loop keeps (and skips otherwise,
 * `except Exception: continue`). Python 3.11+ fromisoformat also tolerates
 * extended ISO forms (compact "20260610", week dates) that this rejects, but
 * the UI's <input type="date"> only ever emits YYYY-MM-DD, so the shared
 * domain is the strict form.
 */
function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const parsed = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  // Reject roll-over dates like 2026-02-31, which Python's fromisoformat refuses.
  if (
    parsed.getUTCFullYear() !== Number(y) ||
    parsed.getUTCMonth() !== Number(m) - 1 ||
    parsed.getUTCDate() !== Number(d)
  ) {
    return null;
  }
  return parsed;
}

function formatIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * DAY_MS);
}

/** Backend `_add_business_days`: walk one day at a time, counting only Mon–Fri. */
function addBusinessDays(anchor: Date, offsetDays: number): Date {
  if (offsetDays === 0) return anchor;
  const step = offsetDays > 0 ? 1 : -1;
  let remaining = Math.abs(offsetDays);
  let current = anchor;
  while (remaining > 0) {
    current = addDays(current, step);
    const weekday = current.getUTCDay(); // 0 = Sunday … 6 = Saturday
    if (weekday >= 1 && weekday <= 5) {
      remaining -= 1;
    }
  }
  return current;
}

/** Backend `_calculate_planned_date`: business_days walks weekdays, everything else is plain days. */
function calculatePlannedDate(anchor: Date, offsetDays: number, offsetType: string): Date {
  if ((offsetType || 'absolute').toLowerCase() === 'business_days') {
    return addBusinessDays(anchor, offsetDays);
  }
  return addDays(anchor, offsetDays);
}

/** Today as a UTC calendar date — the local-clock date, like Python's `date.today()`. */
function todayAsDate(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

const BUILT_IN_ANCHOR_KEYS = new Set([
  'projectStart', 'project_start',
  'constructionDate', 'construction_date',
  'ifcDate', 'ifc_date',
]);

/**
 * Compute the preview timeline for an unsaved template, mirroring
 * `preview_workflow_template` field for field (minus templateId/tenant, which
 * only exist server-side). Throws on an unparseable built-in anchor date —
 * the same inputs make the backend raise.
 */
export function computeLocalTemplatePreview(input: LocalPreviewInput): LocalPreviewResult {
  const { anchors } = input;

  const parseBuiltIn = (label: string, value: string | undefined): Date | null => {
    if (!value) return null;
    const parsed = parseIsoDate(value);
    if (!parsed) throw new Error(`Invalid ${label} date: ${value}`);
    return parsed;
  };

  const projectStartStr = anchors.projectStart || anchors.project_start;
  const constructionDateStr = anchors.constructionDate || anchors.construction_date;
  const ifcDateStr = anchors.ifcDate || anchors.ifc_date;

  // Custom anchors: every non-built-in key that parses as a date; the rest are skipped.
  const customAnchorDates: Record<string, Date> = {};
  Object.entries(anchors).forEach(([key, value]) => {
    if (BUILT_IN_ANCHOR_KEYS.has(key) || value === undefined) return;
    const parsed = parseIsoDate(value);
    if (parsed) customAnchorDates[key] = parsed;
  });

  // Missing built-ins default from today exactly as the backend does:
  // projectStart = today, constructionDate = today + 60, ifcDate = today + 30.
  const today = todayAsDate();
  const projectStart = parseBuiltIn('projectStart', projectStartStr) ?? today;
  const constructionDate = parseBuiltIn('constructionDate', constructionDateStr) ?? addDays(today, 60);
  const ifcDate = parseBuiltIn('ifcDate', ifcDateStr) ?? addDays(today, 30);

  // Steps are consumed in step order (the backend's ORDER BY step_order ASC);
  // the sort is stable so ties keep the caller's order, like SQL does.
  const orderedSteps = [...input.steps].sort((a, b) => a.stepOrder - b.stepOrder);

  const timeline: LocalPreviewTimelineEntry[] = [];
  let previousDate = projectStart;

  orderedSteps.forEach((step) => {
    const anchorType = (step.anchorType || step.offsetType || 'absolute').toLowerCase();
    const offsetType = anchorType;
    const offsetDays = step.offsetDays || 0; // backend: `offset_days or 0`

    let anchorDate: Date;
    if (offsetType === 'after_project_start' || offsetType === 'absolute') {
      anchorDate = projectStart;
    } else if (offsetType === 'before_ifc') {
      anchorDate = ifcDate;
    } else if (offsetType === 'before_construction') {
      anchorDate = constructionDate;
    } else if (offsetType === 'after_previous_step') {
      anchorDate = previousDate;
    } else if (offsetType.startsWith('anchor:')) {
      // Backend replaces EVERY "anchor:" occurrence; slugified keys can never
      // contain a second one, so a prefix strip is equivalent on the real domain.
      const customKey = offsetType.slice('anchor:'.length);
      anchorDate = customAnchorDates[customKey] ?? projectStart;
    } else if (Object.prototype.hasOwnProperty.call(customAnchorDates, offsetType)) {
      // A bare custom key used directly as the offset type.
      anchorDate = customAnchorDates[offsetType];
    } else {
      anchorDate = projectStart;
    }

    const plannedDate = calculatePlannedDate(anchorDate, offsetDays, offsetType);

    // Duration-less steps (null OR zero) get endDate null — the backend's
    // truthy check — and render milestone-style.
    const endDate = step.durationDays ? addDays(plannedDate, step.durationDays) : null;

    timeline.push({
      stepId: step.stepId,
      stepOrder: step.stepOrder,
      name: step.name,
      discipline: step.discipline ?? null,
      category: step.category ?? null,
      offsetType: step.offsetType,
      offsetDays: step.offsetDays,
      durationDays: step.durationDays ?? null,
      plannedDate: formatIsoDate(plannedDate),
      endDate: endDate ? formatIsoDate(endDate) : null,
      anchorType,
    });

    previousDate = plannedDate;
  });

  // Sorted by plannedDate (stable, so same-day steps keep step order) — ISO
  // strings compare like the dates they name.
  const timelineSorted = [...timeline].sort((a, b) =>
    a.plannedDate < b.plannedDate ? -1 : a.plannedDate > b.plannedDate ? 1 : 0
  );

  // Overlap warnings walk the SORTED timeline: a step starting before the
  // previous sorted step's endDate overlaps it; endDate-less steps never block.
  const warnings: string[] = [];
  for (let i = 1; i < timelineSorted.length; i += 1) {
    const currentStep = timelineSorted[i];
    const prevStep = timelineSorted[i - 1];
    if (prevStep.endDate && currentStep.plannedDate < prevStep.endDate) {
      warnings.push(
        `Step '${currentStep.name}' overlaps with previous step '${prevStep.name}'`
      );
    }
  }

  return {
    templateName: input.templateName,
    anchors: {
      projectStart: formatIsoDate(projectStart),
      constructionDate: formatIsoDate(constructionDate),
      ifcDate: formatIsoDate(ifcDate),
      ...Object.fromEntries(
        Object.entries(customAnchorDates).map(([key, value]) => [key, formatIsoDate(value)])
      ),
    },
    timeline: timelineSorted,
    warnings,
    sept_tenantid: '',
  };
}
